import {
  ASSOCIATION_CHANNEL,
  adminEventSchema,
  type AdminEvent,
  type EventKind,
} from '@growup/site-shared';
import {
  Button,
  Card,
  Checkbox,
  DateTimeInput,
  Field,
  Input,
  Notice,
  Select,
  Textarea,
} from '@growup/ui';
import { useState, type FormEvent } from 'react';

import { ApiError, messageOf, send } from '../api';
import { IMAGE_ASPECTS, ImageField, useImagesBusy } from '../media/ImageUpload';
import { isoToLisbonInput, lisbonInputToIso } from './labels';

const autoSlug = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/** `''` = sem limite; senao um inteiro positivo. `undefined` = invalido. */
function parseLimit(text: string): number | null | undefined {
  const t = text.trim();
  if (t === '') return null;
  return /^\d+$/.test(t) && Number(t) > 0 ? Number(t) : undefined;
}

const limitText = (n: number | null) => (n === null ? '' : String(n));

/** Create or edit an event, then publish or cancel it. New events start as drafts. */
export function EventForm({
  event,
  onSaved,
}: {
  event: AdminEvent | null;
  onSaved: (e: AdminEvent) => void;
}) {
  const [kind, setKind] = useState<EventKind>(event?.kind ?? 'standard');
  const [game, setGame] = useState(event?.game ?? '');
  const [channel, setChannel] = useState(
    event?.channel && event.channel !== ASSOCIATION_CHANNEL ? event.channel : '',
  );
  const party = kind === 'stream_party';
  const [title, setTitle] = useState(event?.title ?? '');
  const [slug, setSlug] = useState(event?.slug ?? '');
  const [summary, setSummary] = useState(event?.summary ?? '');
  const [description, setDescription] = useState(event?.description ?? '');
  const [imageUrl, setImageUrl] = useState<string | null>(event?.imageUrl ?? null);
  const [startsAt, setStartsAt] = useState(isoToLisbonInput(event?.startsAt ?? null));
  const [endsAt, setEndsAt] = useState(isoToLisbonInput(event?.endsAt ?? null));
  const [venueName, setVenueName] = useState(event?.venueName ?? '');
  const [venueAddress, setVenueAddress] = useState(event?.venueAddress ?? '');
  const [mapUrl, setMapUrl] = useState(event?.mapUrl ?? '');
  const [capacity, setCapacity] = useState(limitText(event?.capacity ?? null));
  const [volunteersWanted, setVolunteersWanted] = useState(event?.volunteersWanted ?? true);
  const [volunteerSlots, setVolunteerSlots] = useState(limitText(event?.volunteerSlots ?? null));
  const [streamersWanted, setStreamersWanted] = useState(event?.streamersWanted ?? true);
  const [streamerSlots, setStreamerSlots] = useState(limitText(event?.streamerSlots ?? null));
  const [opensAt, setOpensAt] = useState(isoToLisbonInput(event?.registrationOpensAt ?? null));
  const [closesAt, setClosesAt] = useState(isoToLisbonInput(event?.registrationClosesAt ?? null));
  const [cancelReason, setCancelReason] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const photos = useImagesBusy();
  const cancelled = event?.status === 'cancelled';

  const run = async (action: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      onSaved(adminEventSchema.parse(await action()));
      setSaved(done);
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const problems: Record<string, string> = {};
    const limits = {
      capacity: parseLimit(capacity),
      volunteerSlots: parseLimit(volunteerSlots),
      streamerSlots: parseLimit(streamerSlots),
    };
    for (const [key, value] of Object.entries(limits)) {
      if (value === undefined) problems[key] = 'Um número inteiro, ou vazio para sem limite.';
    }
    const starts = lisbonInputToIso(startsAt);
    const ends = lisbonInputToIso(endsAt);
    if (starts === null) problems.startsAt = 'Indique a data e a hora.';
    if (ends === null) problems.endsAt = 'Indique a data e a hora.';
    setFields(problems);
    if (Object.keys(problems).length > 0) return;
    const body = {
      kind,
      game: party ? game : null,
      channel: party && channel.trim() !== '' ? channel : null,
      slug,
      title,
      summary,
      description,
      imageUrl,
      startsAt: starts,
      endsAt: ends,
      venueName,
      venueAddress,
      mapUrl,
      ...limits,
      volunteersWanted,
      streamersWanted,
      registrationOpensAt: lisbonInputToIso(opensAt),
      registrationClosesAt: lisbonInputToIso(closesAt),
    };
    await run(
      () =>
        event
          ? send('PUT', `/api/site/events/${event.id}`, body)
          : send('POST', '/api/site/events', body),
      event ? 'Guardado.' : 'Criado como rascunho. Publique quando estiver pronto.',
    );
  };

  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <Card headingLevel={2} title={event ? 'Dados do evento' : 'Novo evento'}>
        <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
          <fieldset disabled={cancelled} className="flex flex-col gap-4">
            {event ? null : (
              <Field
                label="Tipo"
                hint={
                  party
                    ? 'Vários streamers a jogar juntos, em direto no canal da Grow uP. Online e só para streamers.'
                    : undefined
                }
                className="max-w-sm"
              >
                <Select value={kind} onChange={(e) => setKind(e.target.value as EventKind)}>
                  <option value="standard">Evento</option>
                  <option value="stream_party">Stream party</option>
                </Select>
              </Field>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Título" error={fields.title}>
                <Input
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    if (!event) setSlug(autoSlug(e.target.value));
                  }}
                />
              </Field>
              <Field label="Endereço" hint={`growup.gg/eventos/${slug || '…'}`} error={fields.slug}>
                <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
              </Field>
              <Field label="Início" hint="Hora de Lisboa." error={fields.startsAt}>
                <DateTimeInput value={startsAt} onChange={setStartsAt} />
              </Field>
              <Field label="Fim" error={fields.endsAt}>
                <DateTimeInput value={endsAt} onChange={setEndsAt} />
              </Field>
              {party ? (
                <>
                  <Field label="Jogo" error={fields.game}>
                    <Input value={game} maxLength={80} onChange={(e) => setGame(e.target.value)} />
                  </Field>
                  <Field
                    label="Canal na Twitch"
                    optional
                    hint={`Vazio = o da Grow uP (twitch.tv/${ASSOCIATION_CHANNEL}).`}
                    error={fields.channel}
                  >
                    <Input value={channel} onChange={(e) => setChannel(e.target.value)} />
                  </Field>
                </>
              ) : (
                <>
                  <Field label="Local" error={fields.venueName}>
                    <Input value={venueName} onChange={(e) => setVenueName(e.target.value)} />
                  </Field>
                  <Field label="Morada" optional error={fields.venueAddress}>
                    <Input value={venueAddress} onChange={(e) => setVenueAddress(e.target.value)} />
                  </Field>
                  <Field label="Mapa (URL)" optional error={fields.mapUrl}>
                    <Input value={mapUrl} onChange={(e) => setMapUrl(e.target.value)} />
                  </Field>
                </>
              )}
              <ImageField
                label="Imagem"
                optional
                value={imageUrl}
                onChange={setImageUrl}
                aspect={IMAGE_ASPECTS.event}
                onBusyChange={photos.track('imagem')}
                error={fields.imageUrl}
              />
            </div>
            <Field
              label="Resumo"
              optional
              hint="Uma ou duas frases, para a lista de eventos."
              error={fields.summary}
            >
              <Input value={summary} onChange={(e) => setSummary(e.target.value)} />
            </Field>
            <Field
              label="Descrição"
              optional
              hint="Parágrafos separados por uma linha em branco."
              error={fields.description}
            >
              <Textarea
                rows={6}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>

            <h3 className="font-sub text-label font-bold uppercase text-gu-ink-muted">
              Inscrições
            </h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {party ? (
                <Field
                  label="Máximo de streamers"
                  optional
                  hint="Vazio = sem limite. O staff aprova cada um."
                  error={fields.streamerSlots}
                >
                  <Input
                    inputMode="numeric"
                    value={streamerSlots}
                    onChange={(e) => setStreamerSlots(e.target.value)}
                  />
                </Field>
              ) : (
                <Field
                  label="Lotação de visitantes"
                  optional
                  hint="Vazio = sem limite. Cheio, entram em lista de espera."
                  error={fields.capacity}
                >
                  <Input
                    inputMode="numeric"
                    value={capacity}
                    onChange={(e) => setCapacity(e.target.value)}
                  />
                </Field>
              )}
              <Field
                label="Abrem em"
                optional
                hint="Vazio = já."
                error={fields.registrationOpensAt}
              >
                <DateTimeInput value={opensAt} onChange={setOpensAt} />
              </Field>
              <Field
                label="Fecham em"
                optional
                hint="Vazio = quando o evento começa."
                error={fields.registrationClosesAt}
              >
                <DateTimeInput value={closesAt} onChange={setClosesAt} />
              </Field>
            </div>
            {party ? null : (
              <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2">
                <Checkbox
                  label="Procura voluntários"
                  hint="Só membros e associados, ou jovens inscritos pelo encarregado."
                  checked={volunteersWanted}
                  onChange={(e) => setVolunteersWanted(e.target.checked)}
                />
                {volunteersWanted ? (
                  <Field
                    label="Voluntários a aprovar"
                    optional
                    hint="Vazio = sem limite."
                    error={fields.volunteerSlots}
                  >
                    <Input
                      inputMode="numeric"
                      value={volunteerSlots}
                      onChange={(e) => setVolunteerSlots(e.target.value)}
                    />
                  </Field>
                ) : (
                  <span />
                )}
                <Checkbox
                  label="Procura streamers"
                  hint="Só streamers da Grow uP no Stream Portal."
                  checked={streamersWanted}
                  onChange={(e) => setStreamersWanted(e.target.checked)}
                />
                {streamersWanted ? (
                  <Field
                    label="Streamers a aprovar"
                    optional
                    hint="Vazio = sem limite."
                    error={fields.streamerSlots}
                  >
                    <Input
                      inputMode="numeric"
                      value={streamerSlots}
                      onChange={(e) => setStreamerSlots(e.target.value)}
                    />
                  </Field>
                ) : null}
              </div>
            )}
          </fieldset>
          {error ? <Notice tone="danger">{error}</Notice> : null}
          {saved ? <Notice tone="success">{saved}</Notice> : null}
          {!cancelled ? (
            <div>
              <Button type="submit" disabled={busy || photos.busy}>
                {event ? 'Guardar' : 'Criar rascunho'}
              </Button>
            </div>
          ) : null}
        </form>
      </Card>

      {event ? (
        <Card headingLevel={2} title="Publicação">
          <div className="flex flex-col gap-3">
            {event.status === 'draft' ? (
              <>
                <p className="text-gu-ink-muted">
                  Rascunho: ainda não aparece no site. Ao publicar, fica em growup.gg/eventos e as
                  inscrições abrem.
                </p>
                <Button
                  disabled={busy}
                  onClick={() =>
                    void run(
                      () => send('POST', `/api/site/events/${event.id}/publish`),
                      'Publicado.',
                    )
                  }
                >
                  Publicar
                </Button>
              </>
            ) : null}
            {event.status === 'published' ? (
              <p className="text-gu-ink-muted">
                Publicado e visível em growup.gg/eventos/{event.slug}.
              </p>
            ) : null}
            {event.status === 'cancelled' ? (
              <Notice tone="danger" title="Evento cancelado">
                {event.cancelReason ?? 'Sem motivo indicado.'}
              </Notice>
            ) : (
              <div className="flex flex-col gap-2 border-t border-gu-line pt-3">
                <Field
                  label="Motivo do cancelamento"
                  optional
                  hint="Vai no email a quem está inscrito."
                >
                  <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                </Field>
                <Checkbox
                  label="Confirmo: cancelar o evento e avisar todos os inscritos"
                  checked={confirmCancel}
                  onChange={(e) => setConfirmCancel(e.target.checked)}
                />
                <Button
                  variant="secondary"
                  disabled={busy || !confirmCancel}
                  onClick={() =>
                    void run(
                      () =>
                        send('POST', `/api/site/events/${event.id}/cancel`, {
                          reason: cancelReason.trim() || null,
                        }),
                      'Evento cancelado. Os inscritos foram avisados por email.',
                    )
                  }
                >
                  Cancelar evento
                </Button>
              </div>
            )}
          </div>
        </Card>
      ) : null}
      {event ? <DuplicateCard event={event} onSaved={onSaved} /> : null}
    </div>
  );
}

/**
 * Duplicar (GS17): um rascunho igual, noutro endereco e noutra data, com o
 * Espaco e os Turnos (ou o alinhamento) sem pessoas.
 */
function DuplicateCard({
  event,
  onSaved,
}: {
  event: AdminEvent;
  onSaved: (event: AdminEvent) => void;
}) {
  const [slug, setSlug] = useState(`${event.slug}-2`);
  const [startsAt, setStartsAt] = useState(isoToLisbonInput(event.startsAt));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  const duplicate = async () => {
    const starts = lisbonInputToIso(startsAt);
    if (starts === null) {
      setFields({ startsAt: 'Indique a data e a hora.' });
      return;
    }
    setBusy(true);
    setError(null);
    setFields({});
    try {
      onSaved(
        adminEventSchema.parse(
          await send('POST', `/api/site/events/${event.id}/duplicate`, {
            slug: autoSlug(slug),
            startsAt: starts,
          }),
        ),
      );
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card headingLevel={2} title="Duplicar" className="lg:col-start-2">
      <div className="flex flex-col gap-3">
        <p className="text-gu-ink-muted">
          {event.kind === 'stream_party'
            ? 'Nova stream party igual a esta, com o alinhamento sem pessoas.'
            : 'Novo evento igual a este, com o espaço e os turnos sem pessoas.'}{' '}
          Fica em rascunho; o roster começa vazio.
        </p>
        <Field label="Endereço" hint={`growup.gg/eventos/${slug || '…'}`} error={fields.slug}>
          <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
        </Field>
        <Field label="Começa em" hint="A duração fica igual." error={fields.startsAt}>
          <DateTimeInput value={startsAt} onChange={setStartsAt} />
        </Field>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <div>
          <Button variant="secondary" disabled={busy} onClick={() => void duplicate()}>
            Duplicar
          </Button>
        </div>
      </div>
    </Card>
  );
}
